import { CartAddEvent } from '@theme/events';

class FlaayFloatingVideos extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.slides = [...this.querySelectorAll('.ffv-slide')];
    if (!this.slides.length) return;
    this.dialog = this.querySelector('dialog');
    this.launcher = this.querySelector('.ffv-launcher');
    this.preview = this.querySelector('.ffv-launch video');
    this.index = 0;
    this.muted = true;
    this.preview.poster = this.slides[0].dataset.poster || '';
    this.querySelector('.ffv-launch-count').textContent = `${this.slides.length} video${this.slides.length > 1 ? 's' : ''}`;
    this.querySelector('.ffv-launch').addEventListener('click', () => this.open(), options);
    this.querySelector('.ffv-dismiss').addEventListener('click', () => {
      this.dismissed = true;
      this.launcher.hidden = true;
      this.stopAll();
    }, options);
    this.querySelectorAll('[data-action]').forEach(button => button.addEventListener('click', () => {
      const action = button.dataset.action;
      if (action === 'close') this.dialog.close();
      if (action === 'next') this.show(this.index + 1);
      if (action === 'prev') this.show(this.index - 1);
      if (action === 'sound') {
        this.muted = !this.muted;
        this.currentVideo.muted = this.muted;
        button.textContent = this.muted ? 'Sound off' : 'Sound on';
        button.setAttribute('aria-label', this.muted ? 'Unmute video' : 'Mute video');
      }
      if (action === 'play') {
        if (this.currentVideo.paused) this.play(this.currentVideo);
        else this.currentVideo.pause();
      }
    }, options));
    this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); }, options);
    this.dialog.addEventListener('close', () => {
      this.stopAll();
      this.querySelector('.ffv-launch').focus({ preventScroll: true });
    }, options);
    this.dialog.addEventListener('keydown', event => {
      if (event.target.closest('.ffv-products') || event.target.matches('select,input')) return;
      if (event.key === 'ArrowRight') { event.preventDefault(); this.show(this.index + 1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.show(this.index - 1); }
    }, options);
    this.slides.forEach(slide => {
      const video = slide.querySelector('video');
      for (const name of ['play', 'pause']) video.addEventListener(name, () => {
        if (video !== this.currentVideo) return;
        const b = this.querySelector('[data-action="play"]');
        b.textContent = video.paused ? 'Play' : 'Pause';
        b.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
      }, options);
      video.addEventListener('error', () => { slide.querySelector('.ffv-status').textContent = 'Video could not load. Please try again.'; }, options);
      video.addEventListener('pointerdown', e => { this.start = {x:e.clientX,y:e.clientY}; }, options);
      video.addEventListener('pointerup', e => {
        if (!this.start) return;
        const dx=e.clientX-this.start.x, dy=e.clientY-this.start.y;
        this.start=null;
        if (Math.max(Math.abs(dx),Math.abs(dy))>50) this.show(this.index + ((Math.abs(dx)>Math.abs(dy)?dx:dy)<0?1:-1));
      }, options);
      video.addEventListener('pointercancel', () => { this.start=null; }, options);
      slide.querySelectorAll('.ffv-product').forEach(card => {
        card.querySelector('form').addEventListener('submit', e => this.add(e,card), options);
        card.querySelector('[name="id"]').addEventListener('change', () => this.price(card), options);
        this.price(card);
      });
    });
    document.addEventListener('visibilitychange', () => { if (document.hidden) this.stopAll(); }, options);
    window.addEventListener('pagehide', () => this.stopAll(), options);
    // Avoid competing video playback when another theme dialog is open.
    this.modalObserver = new MutationObserver(() => {
      if (document.querySelector('dialog[open]') && !this.dialog.open) this.preview.pause();
    });
    this.modalObserver.observe(document.body,{subtree:true,attributes:true,attributeFilter:['open']});
    const startPreview = () => {
      if (!this.isConnected || this.dismissed || this.interacted || document.hidden || document.querySelector('dialog[open]')) return;
      if (this.dataset.autoplay !== 'true' || navigator.connection?.saveData || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.preview.src = this.slides[0].dataset.video;
      this.play(this.preview);
    };
    if (document.readyState === 'complete') this.timer=setTimeout(startPreview,1000);
    else window.addEventListener('load', () => { this.timer=setTimeout(startPreview,1000); }, {...options,once:true});
  }
  get currentVideo() { return this.slides[this.index].querySelector('video'); }
  play(video) { const p=video.play(); if (p?.catch) p.catch(() => {
    if (video === this.currentVideo) this.querySelector('[data-action="play"]').textContent='Play';
  }); }
  stopAll() { this.querySelectorAll('video').forEach(v => v.pause()); }
  open() { this.interacted=true;clearTimeout(this.timer);this.preview.pause(); if (!this.dialog.open) this.dialog.showModal(); this.show(this.index); }
  show(index) {
    this.stopAll();
    this.index = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide,i) => { slide.hidden = i !== this.index; });
    const slide=this.slides[this.index],video=this.currentVideo;
    if (!video.getAttribute('src')) { video.poster=slide.dataset.poster || '';video.src=slide.dataset.video; }
    video.muted=this.muted;
    this.querySelectorAll('.ffv-nav button').forEach(b => { b.hidden=this.slides.length<2; });
    if (this.dialog.open && !document.hidden) this.play(video);
  }
  price(slide) {
    const variants=JSON.parse(slide.querySelector('.ffv-variants').textContent);
    const id=slide.querySelector('[name="id"]').value;
    const selected=variants.find(v => String(v.id)===id);
    if (!selected) return;
    slide.querySelector('.ffv-price').textContent=selected.price;
    const compare=slide.querySelector('.ffv-compare');compare.textContent=selected.compare;compare.hidden=!selected.compare;
    const button=slide.querySelector('[type="submit"]');button.disabled=!selected.available;button.textContent=selected.available?'ADD TO CART':'SOLD OUT';
  }
  async add(event,slide) {
    event.preventDefault();
    if (this.busy) return;
    const form=event.currentTarget;
    if (!form.reportValidity()) return;
    const id=form.elements.id.value;
    if (!id) return;
    this.busy=true;
    const button=form.querySelector('[type="submit"]'),status=slide.querySelector('.ffv-status');
    button.disabled=true;button.textContent='ADDING…';status.textContent='';
    try {
      const sections=[...new Set([...document.querySelectorAll('cart-items-component')].map(e=>e.dataset.sectionId).filter(Boolean))].slice(0,5);
      const root=window.Shopify?.routes?.root || '/';
      const response=await fetch(`${root}cart/add.js`,{method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},body:JSON.stringify({items:[{id,quantity:1}],sections,sections_url:location.pathname})});
      const data=await response.json();
      if (!response.ok || data.status) throw new Error(typeof data.description==='string'?data.description:'Unable to add this item. Please try again.');
      this.dispatchEvent(new CartAddEvent({},id,{source:'flaay-floating-videos',itemCount:1,productId:form.dataset.productId,sections:data.sections}));
      this.dialog.close();
      document.dispatchEvent(new CustomEvent('flaay:cart-added',{detail:data}));
      status.textContent='Added to your cart.';
    } catch(error) { status.textContent=error.message || 'Unable to add this item. Please try again.'; }
    finally { this.busy=false;this.price(slide); }
  }
  disconnectedCallback() {
    clearTimeout(this.timer);this.abort?.abort();this.abort=null;this.modalObserver?.disconnect();this.stopAll();
    if (this.dialog?.open) this.dialog.close();
  }
}
if (!customElements.get('flaay-floating-videos')) customElements.define('flaay-floating-videos',FlaayFloatingVideos);
